/*
  Список сформированных документов и выгрузка их в файл.

  🔴 Показа текста на экране здесь НЕТ, и это решение владельца 09.09.2026.
  Экран показывал документ сплошным текстом в моноширинном блоке, а файл - с
  полями, таблицей и переносами: два разных вида одного документа. Человек
  сверял по одному, а в суд нёс другой. Правило простое: у документа один вид -
  тот, в котором он уйдёт адресату, и смотрят его в файле.

  Возвращать показ текста нельзя, не вернув вместе с ним и расхождение.

  Каждый документ несёт версию шаблона и версию правового пакета: через год,
  когда должник придёт оспаривать приказ, мы обязаны показать ровно тот текст,
  что ушёл в суд, и объяснить его по правилам того времени (§3.2.5, И2).

  Пока шаблоны не подписаны юристом, документ помечен черновиком. Это не
  украшение: подавать по неутверждённому шаблону нельзя, и человек должен
  видеть это до того, как понесёт пакет в суд.

  09.09.2026: добавлена выгрузка в файл. Владелец сформировал пакет и не нашёл
  документов - их и нельзя было унести с экрана, при том что §3.2.6 требует
  ровно этого: выгрузить, подписать электронной подписью, загрузить в
  государственную систему. Читаемое имя файла собирается здесь: в заголовке
  ответа сервера кириллица разбирается браузерами по-разному.

  Список используется в двух местах - на карточке счёта (заявления и расчёты) и
  на конвейере (реестры волн), - поэтому заголовок и пустое состояние приходят
  пропсами: пустое состояние обязано объяснять, что появится ЗДЕСЬ, а не вообще.
*/

import { useState } from 'react'

import { api } from '../api/client.js'
import { date } from '../api/format.js'
import { Confirm, Empty, Notice, Tag } from './Common.jsx'
import PageBar from './PageBar.jsx'
import usePaging from './usePaging.js'

const KIND_TITLES = {
  court_order_application: 'Заявление о вынесении судебного приказа',
  lawsuit_application: 'Исковое заявление',
  debt_calculation: 'Расчёт задолженности и пеней',
  filing_registry: 'Реестр волны для подачи',
  debtor_notice: 'Уведомление должнику',
}

/*
  Сколько документов показывать сразу. Список не обрезался ничем: на счёте, по
  которому пакет собирали много раз, в нём накопилось около полутора сотен
  записей и карточка счёта вытянулась на девять тысяч двести пикселей - историю
  смен стадий и расшифровку долга приходилось искать прокруткой мимо них (замер
  09.09.2026). Смотрят при этом последние: те, что пойдут в суд.

  21.10.2026: самодельное «Показать ещё» (по пять) заменено общей панелью страниц.
  Наименьший размер страницы там 10, поэтому по умолчанию берётся он, а не 25, как у
  остальных списков: карточка счёта не должна снова вытянуться. Выбранный человеком
  размер запоминается один на все списки и этого умолчания не трогает.
*/
const DEFAULT_PAGE_SIZE = 10

/*
  Знаки, которых не бывает в именах файлов Windows. Заголовок документа
  приходит с сервера и содержит номер счёта, а в номере встречается косая черта
  - с ней браузер сохранил бы файл под обрезанным именем или не сохранил вовсе.
*/
const FORBIDDEN_IN_FILENAME = /[\\/:*?"<>|]/g

function filenameFor(doc, format) {
  const title = KIND_TITLES[doc.kind] ?? doc.title
  return `${title} от ${date(doc.as_of)}`.replace(FORBIDDEN_IN_FILENAME, '-') + `.${format}`
}

export default function DocumentList({
  documents,
  // Панель страниц, если страницы режет сервер (документы счёта - 21.10.2026, реестры волн
  // на конвейере - 23.10.2026): тогда `documents` - уже ОДНА страница, а панель - свойства
  // `usePagedList`. Без неё список целый и режется здесь же; в кабинете так его больше не
  // зовёт ни одно место, остались проверки вида строки, которым страницы не нужны.
  paging,
  title = 'Документы',
  emptyTitle = 'Документов пока нет',
  empty,
  // Кнопка удаления появляется, только когда список знает, что делать после
  // удаления: перечитать карточку счёта или список реестров. Без этого
  // удалённый документ остался бы на экране до перезагрузки страницы.
  onDeleted,
}) {
  const [error, setError] = useState(null)
  const client = usePaging(paging ? null : documents, DEFAULT_PAGE_SIZE)
  const rows = paging ? documents : client.rows
  const bar = paging ?? client.bar
  const [saving, setSaving] = useState(null)
  const [removing, setRemoving] = useState(null)
  const [busy, setBusy] = useState(false)

  /*
    Отказ показывается словами и не оставляет кнопку в состоянии ожидания:
    молчаливая кнопка читается как поломка кабинета, а не как отказ сервера.
  */
  async function save(doc, format) {
    setError(null)
    setSaving(`${doc.document_id}:${format}`)
    try {
      await api.downloadDocument(doc.document_id, format, filenameFor(doc, format))
    } catch (failure) {
      setError(failure)
    } finally {
      setSaving(null)
    }
  }

  /*
    Отказ сервера остаётся ВНУТРИ окна подтверждения, а не уходит наверх
    (правило 10 скилла `frontend`): «счёт отмечен поданным» человек обязан
    прочитать там, где нажал «Удалить», иначе окно закрывается молча и выглядит
    так, будто документ удалён.
  */
  async function remove() {
    setBusy(true)
    setError(null)
    try {
      await api.deleteDocument(removing.document_id)
      setRemoving(null)
      onDeleted()
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="section">
      <h2 className="section__title">{title}</h2>
      {/* Ошибка окна показывается в самом окне; сюда попадает только то, что
          случилось вне его, - отказ выгрузки файла. */}
      <Notice error={removing ? null : error} />

      {bar.total === 0 ? (
        <Empty title={emptyTitle}>
          {empty ?? (
            <>
              Нажмите «Сформировать пакет» - Арбитр ЖКХ соберёт заявление и расчёт по каждому
              требованию, вытекающему из счёта. При долевой собственности это отдельное заявление
              на каждого сособственника.
            </>
          )}
        </Empty>
      ) : (
        <div className="card">
          <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Документ</th>
                <th>Собран</th>
                <th>Версии</th>
                <th>Состояние</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((doc) => (
                <tr key={doc.document_id}>
                  <td>{KIND_TITLES[doc.kind] ?? doc.title}</td>
                  <td data-label="Собран">{date(doc.as_of)}</td>
                  <td className="muted" data-label="Версии">
                    шаблон {doc.template_version} · право {doc.legal_package_version}
                  </td>
                  <td className="table__block" data-label="Состояние">
                    {doc.is_releasable ? (
                      <Tag kind="calm">готов к подаче</Tag>
                    ) : (
                      <Tag kind="warn">черновик: шаблон не подписан юристом</Tag>
                    )}
                    {doc.warnings.map((warning) => (
                      <div className="muted" key={warning}>
                        {warning}
                      </div>
                    ))}
                  </td>
                  {/*
                    Кнопки строкой, а не столбиком: столбиком они растягивали
                    строку таблицы вдвое, и пять документов занимали экран
                    целиком (замер 09.09.2026). Перенос запрещён (`row--tight`),
                    иначе браузер ставит две короткие кнопки друг под друга,
                    хотя рядом с ними пустое место.

                    На кнопке короткое «Word», а читалке экрана и проверкам
                    достаётся полное «Скачать в Word»: в колонке «Скачать» формат
                    читается сам, но вне этого ряда одно слово было бы загадкой.
                  */}
                  {/* Подпись ячейки видна на узком экране, где строка
                      разворачивается в карточку (правило 6е). С появлением
                      удаления «Скачать» перестало описывать содержимое: под ним
                      три кнопки, и одна из них не скачивает (осмотр
                      14.09.2026). */}
                  <td data-label="Действия">
                    <div className="row row--tight">
                      <button
                        type="button"
                        className="button button--quiet"
                        aria-label="Скачать в Word"
                        disabled={saving !== null}
                        onClick={() => save(doc, 'docx')}
                      >
                        {saving === `${doc.document_id}:docx` ? 'Готовим...' : 'Word'}
                      </button>
                      <button
                        type="button"
                        className="button button--quiet"
                        aria-label="Скачать в PDF"
                        disabled={saving !== null}
                        onClick={() => save(doc, 'pdf')}
                      >
                        {saving === `${doc.document_id}:pdf` ? 'Готовим...' : 'PDF'}
                      </button>
                      {/*
                        Удаление стоит последним и спрашивает подтверждение:
                        текст документа лежит в самой записи, и вернуть его
                        неоткуда. Право на удаление то же, что на сборку пакета,
                        и проверяет его сервер - здесь кнопка появляется вместе
                        с обработчиком, который знает, что перечитать.
                      */}
                      {onDeleted ? (
                        <button
                          type="button"
                          className="button button--quiet"
                          aria-label={`Удалить документ «${KIND_TITLES[doc.kind] ?? doc.title}»`}
                          disabled={saving !== null}
                          onClick={() => {
                            setError(null)
                            setRemoving(doc)
                          }}
                        >
                          Удалить
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <PageBar {...bar} what="документов" />
        </div>
      )}

      {removing ? (
        <Confirm
          title="Удалить документ?"
          action="Удалить"
          busy={busy}
          error={error}
          onCancel={() => {
            setRemoving(null)
            setError(null)
          }}
          onConfirm={remove}
        >
          «{KIND_TITLES[removing.kind] ?? removing.title}» от {date(removing.as_of)} исчезнет
          насовсем: текст документа хранится в самой записи, и восстановить его будет неоткуда.
          Формировать пакет заново можно сколько угодно раз.
        </Confirm>
      ) : null}
    </section>
  )
}
